import { Component } from '@theme/component';

/**
 * Colour swatches that drive the product media gallery.
 *
 * Each swatch carries the media id of the image attached to that colour's
 * variant; clicking one moves the gallery slideshow to that slide. Colours
 * with no image attached render as plain text rather than a dead button, so
 * every swatch that looks clickable does something.
 *
 * The slide index is resolved from the DOM at click time rather than baked in
 * from Liquid, because the gallery reorders its media around the selected
 * variant (see snippets/product-media-gallery-content.liquid).
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement[]} [swatches]
 *
 * @extends Component<Refs>
 */
class CcColorOptions extends Component {
  /**
   * Move the product gallery to the media attached to a colour.
   *
   * @param {number|string} mediaId - Media id for the colour's variant.
   * @param {Event} event - The originating click.
   */
  select(mediaId, event) {
    const slideshow = this.#gallery?.slideshow;
    const slides = slideshow?.refs.slides;

    if (!slides?.length) return;

    const index = slides.findIndex((slide) => slide.querySelector(`[data-media-id="${mediaId}"]`));

    if (index < 0) return;

    slideshow.select(index);

    const target = event.target instanceof Element ? event.target.closest('[data-media-id]') : null;
    this.#markSelected(target);
  }

  /**
   * Keep exactly one swatch in the pressed state.
   *
   * @param {Element | null} selected
   */
  #markSelected(selected) {
    for (const swatch of this.refs.swatches ?? []) {
      swatch.setAttribute('aria-pressed', String(swatch === selected));
    }
  }

  /** @returns {import('@theme/media-gallery').MediaGallery | null} */
  get #gallery() {
    return this.closest('.shopify-section')?.querySelector('media-gallery') ?? null;
  }
}

if (!customElements.get('cc-color-options-component')) {
  customElements.define('cc-color-options-component', CcColorOptions);
}
